import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSeoConfig } from '../data/SeoContext';

const isExternalTarget = (target) => /^https?:\/\//i.test(target);
// Internal targets must be a single-slash path — anything else ('//host',
// 'javascript:...') is refused outright, even from a corrupt DB row.
const isSafeInternalTarget = (target) => /^\/(?!\/)/.test(target);

// Applies admin-managed redirects on routes the router already knows (e.g. a
// renamed article at /posts/:id). One hop only; recently redirected *sources*
// are remembered briefly so a misconfigured pair (A -> B, B -> A) can never
// ping-pong the router.
function SiteRedirects() {
    const location = useLocation();
    const navigate = useNavigate();
    const { getRedirectForPath, status } = useSeoConfig();
    const recentRef = useRef(new Map());

    useEffect(() => {
        if (status !== 'ready') {
            return;
        }
        // Admin pages are never redirected — a stale DB row must not be able
        // to lock the admin out of the dashboard.
        if (location.pathname === '/admin' || location.pathname.startsWith('/admin/')) {
            return;
        }
        const target = getRedirectForPath(location.pathname);
        if (!target) {
            return;
        }

        const now = Date.now();
        const recent = recentRef.current;
        [...recent.entries()].forEach(([path, stamp]) => {
            if (now - stamp > 5000) {
                recent.delete(path);
            }
        });
        // If the target itself recently acted as a redirect source, following
        // it would ping-pong — stop here.
        if (recent.has(target)) {
            return;
        }
        recent.set(location.pathname, now);

        if (isExternalTarget(target)) {
            if (target !== window.location.href) {
                window.location.assign(target);
            }
        } else if (isSafeInternalTarget(target)) {
            navigate(target, { replace: true });
        }
    }, [location.pathname, status, getRedirectForPath, navigate]);

    return null;
}

export default SiteRedirects;

// Replaces the router's bare catch-all: an unknown path might be an
// admin-managed redirect, so wait for the SEO config before deciding
// between redirecting and falling back to the homepage.
export function CatchAllRedirect() {
    const location = useLocation();
    const { status, getRedirectForPath } = useSeoConfig();

    const target = getRedirectForPath(location.pathname);
    if (target) {
        if (isExternalTarget(target)) {
            if (target !== window.location.href) {
                window.location.assign(target);
            }
            return null;
        }
        // A target that is itself a redirect source can only be a cycle or
        // corrupt data (the backend refuses chains) — bail to the homepage
        // instead of looping.
        if (!isSafeInternalTarget(target) || getRedirectForPath(target)) {
            return <Navigate to="/" replace />;
        }
        return <Navigate to={target} replace />;
    }
    if (status === 'loading') {
        return <p className="small-meta site-loading">Loading …</p>;
    }
    return <Navigate to="/" replace />;
}